import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/auth';
// Theme toggle removed temporarily
// import { ThemeToggle } from './ThemeToggle';
import BackgroundGrid from './BackgroundGrid';

// Import icons
import { Home, Search, PlusSquare, Heart, User, LogOut, Sparkles, Menu, X, MapPin } from 'lucide-react';

type NavItemProps = {
  to: string;
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  mobileOnly?: boolean;
};

function NavItem({ to, label, icon: Icon, isActive, mobileOnly }: NavItemProps) {
  return (
    <Link 
      to={to} 
      className={`flex flex-1 items-center justify-center py-4 transition-colors border-t border-l border-r border-haus-light-black paper-texture-super-heavy ${
        isActive ? 'text-haus-light-white' : 'text-haus-light-black'
      } ${mobileOnly ? 'md:hidden' : ''}`}
      style={{ 
        backgroundColor: isActive ? 'var(--color-dark-green)' : 'var(--color-cream)',
        transition: 'background-color 0.2s ease-in-out'
      }}
      onMouseOver={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'var(--color-light-white)')}
      onMouseOut={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'var(--color-cream)')}
    >
      <Icon size={18} className="mr-2" />
      <span className="font-medium nav-item uppercase">{label}</span>
    </Link>
  );
}

type LayoutProps = {
  children: React.ReactNode;
};

export function Layout({ children }: LayoutProps) {
  const { signOut } = useAuthContext();
  const location = useLocation();
  const currentPath = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      // Auth context will handle the redirect
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="flex flex-col h-screen paper-texture-super-heavy relative">
      {/* Top Navigation Bar - Desktop - Now FULL width with higher z-index */}
      <div className="hidden md:block w-full border-b border-haus-light-black paper-texture-super-heavy relative z-0">
        <div className="flex ml-20 w-1/2">
          <NavItem 
            to="/discover" 
            label="Discover" 
            icon={Search} 
            isActive={currentPath === '/discover' || currentPath === '/'} 
          />
          <NavItem 
            to="/home" 
            label="My Photos" 
            icon={Home} 
            isActive={currentPath === '/home'} 
          />
          <NavItem 
            to="/try-on" 
            label="Try On" 
            icon={Sparkles} 
            isActive={currentPath === '/try-on'} 
          />
        </div>
      </div>

      {/* Left Brand Panel - Now positioned underneath the nav bar */}
      <div className="fixed top-[62px] left-0 bottom-0 w-20 z-10 hidden md:flex flex-col items-center justify-center border-r border-haus-dark-grey paper-texture-super-heavy">
        {/* Container with top line and ellipse */}
        <div className="flex flex-col items-center">
          {/* Top decorative line */}
          <div className="w-px h-24 bg-haus-dark-grey"></div>
          
          {/* Small spacer */}
          <div className="h-4"></div>
          
          {/* Brand name in elliptical container */}
          <div 
            className="overflow-hidden border-2 border-haus-dark-grey h-72 w-14 flex items-center justify-center"
            style={{ borderRadius: '150px / 200px' }}
          >
            <div className="rotate-180" style={{ writingMode: 'vertical-lr' }}>
              <h1 className="font-display text-haus-light-black text-4xl tracking-widest">STYLOS</h1>
            </div>
          </div>
          
          {/* Small spacer */}
          <div className="h-4"></div>
          
          {/* Bottom decorative line */}
          <div className="w-px h-24 bg-haus-dark-grey"></div>
        </div>
        
        {/* Logout button */}
        <button 
          onClick={handleLogout}
          className="p-3 rounded-full border border-haus-dark-grey bg-transparent text-haus-dark-brown hover:bg-haus-cream transition-colors absolute bottom-8"
          title="Log Out"
        >
          <LogOut size={16} />
        </button>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden flex items-center paper-texture-super-heavy justify-between border-b border-haus-light-black px-4 py-3 bg-haus-light-white/80 backdrop-blur-sm z-10">
        <h1 className="font-display layout-logo text-haus-light-black">STYLOS</h1>
        <button 
          onClick={toggleMobileMenu}
          className="p-2 rounded-full bg-haus-dark-green hover:bg-haus-cream transition-colors"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute inset-0 paper-texture-super-heavy z-50 pt-16 bg-haus-light-white/95">
          {/* Small close button at top right */}
          <button 
            onClick={toggleMobileMenu}
            className="absolute top-4 right-4 p-2 rounded-full bg-haus-dark-green hover:bg-haus-cream transition-colors"
            aria-label="Close menu"
          >
            <X size={18} className="text-haus-light-white" />
          </button>
          
          <nav className="flex flex-col p-4">
            <NavItem 
              to="/discover" 
              label="Discover" 
              icon={Search} 
              isActive={currentPath === '/discover' || currentPath === '/'} 
              mobileOnly
            />
            <NavItem 
              to="/home" 
              label="My Photos" 
              icon={Home} 
              isActive={currentPath === '/home'} 
              mobileOnly
            />
            <NavItem 
              to="/try-on" 
              label="Try On" 
              icon={Sparkles} 
              isActive={currentPath === '/try-on'} 
              mobileOnly
            />
            <div className="border-t border-haus-light-grey mt-4 pt-4">
              <button 
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-4 py-3 rounded-lg transition-colors bg-transparent text-haus-light-black hover:bg-haus-cream"
              >
                <LogOut size={20} />
                <span className="font-medium nav-item">Log Out</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content with Background Grid that scrolls */}
      <div className="flex-1 overflow-auto relative bg-haus-light-white md:pl-20">
        {/* Main wrapper - contains both grid and content */}
        <div className="relative min-h-screen">
          {/* Background Grid Layer */}
          <div className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
            <BackgroundGrid 
              opacity={0.15} 
              showNumbers={true}
              asymmetrical={true}
              fadeAreas={[]}
              color="var(--color-dark-brown)"
            />
          </div>
          
          {/* Content Layer - above grid */}
          <div className="relative mx-auto w-full max-w-4xl px-4 md:px-6 py-4 md:py-8" style={{ zIndex: 2 }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
} 